import { ArrowLeft } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePageTools } from './page-tools';

export interface PageHeaderProps {
  /** The page title, rendered as the one `h1` of the page. Omit it with `loading` while the title is not known yet. */
  title?: ReactNode;
  /** The title is still loading: a placeholder of the same height replaces the `h1`, nothing moves when it arrives. */
  loading?: boolean;
  /** `id` of the `h1`, for `aria-labelledby` on the page's region. */
  headingId?: string;
  /** Optional link above the title to the page's parent (already translated). */
  back?: { to: string; label: string };
  /**
   * Optional trail above the title ("Inhalte / <title>"), already translated; replaces `back`. Items with a
   * `to` are links, the last one (no `to`) is the current page (`aria-current`). `label` names the landmark.
   * On a phone only the last linked item is shown, as the back link.
   */
  breadcrumb?: { label: string; items: ReadonlyArray<{ label: string; to?: string }> };
  /** Optional small label above the title (already translated). */
  overline?: ReactNode;
  /** Inline next to the title: status pill or badges (status is information, never a button). */
  badges?: ReactNode;
  /** One context line below the title (version, campaign, dates). */
  meta?: ReactNode;
  /** One muted sentence below the title/meta. */
  description?: ReactNode;
  /** The actions on the right: exactly one primary action, then secondary controls. */
  actions?: ReactNode;
  /** One small line right-aligned under the actions (a blocked hint); needs `actions`. */
  actionsNote?: ReactNode;
  className?: string;
}

/**
 * The one page header of the app (style guide 2.9b): optional back link and
 * overline, the `h1` (focus target after a navigation, `tabIndex` -1), optional
 * badges, meta line and description, an action slot on the right. Every page
 * renders it in every state (loading, error, not found, empty, loaded) so the
 * heading never changes size or position and the content below never jumps.
 */
export const PageHeader = forwardRef<HTMLHeadingElement, PageHeaderProps>(function PageHeader(
  { title, loading, headingId, back, breadcrumb, overline, badges, meta, description, actions, actionsNote, className },
  ref,
) {
  const tools = usePageTools();
  // On a phone (the frame passes its tools) a trail would repeat the h1 right below it: only its parent stays, as a back link.
  const parent = tools && breadcrumb ? breadcrumb.items.filter((crumb) => crumb.to).at(-1) : undefined;
  const backLink = parent?.to ? { to: parent.to, label: parent.label } : back;
  const trail = breadcrumb && !parent ? (
    <nav className="ui-breadcrumb" aria-label={breadcrumb.label}>
      <ol className="ui-breadcrumb__list">
        {breadcrumb.items.map((crumb, index) => (
          <li key={`${index}-${crumb.label}`} className="ui-breadcrumb__item">
            {crumb.to ? (
              <Link className="ui-breadcrumb__link" to={crumb.to}>
                {crumb.label}
              </Link>
            ) : (
              <span className="ui-breadcrumb__current" aria-current="page">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  ) : backLink ? (
    <Link className="ui-page-header__back" to={backLink.to}>
      <ArrowLeft aria-hidden="true" size={16} />
      {backLink.label}
    </Link>
  ) : null;
  // Without a back link or trail the tools share the title row (S180-1): no empty line above the title.
  const inlineTools = tools && !trail ? <div className="ui-page-head__tools ui-page-header__tools">{tools}</div> : null;
  return (
    <div className="ui-page-head">
      {tools && trail ? (
        // On a phone the frame has no top bar: back link or trail on the left, the frame's tools on the right.
        <div className="ui-page-head__top">
          <div className="ui-page-head__trail">{trail}</div>
          <div className="ui-page-head__tools">{tools}</div>
        </div>
      ) : (
        trail
      )}
      <header className={className ? `ui-page-header ${className}` : 'ui-page-header'}>
        <div className="ui-page-header__main">
          {overline ? <p className="ui-overline ui-page-header__overline">{overline}</p> : null}
          <div className={inlineTools ? 'ui-page-header__titlerow ui-page-header__titlerow--tools' : 'ui-page-header__titlerow'}>
            {loading ? (
              <span className="ui-skeleton ui-skeleton--title" aria-hidden="true" />
            ) : (
              <h1 id={headingId} ref={ref} tabIndex={-1}>
                {title}
              </h1>
            )}
            {badges}
            {inlineTools}
          </div>
          {meta ? <div className="ui-page-header__meta">{meta}</div> : null}
          {description ? <p className="ui-page-header__description">{description}</p> : null}
        </div>
        {actions && actionsNote ? (
          <div className="ui-page-header__side">
            <div className="ui-page-header__actions">{actions}</div>
            <p className="ui-page-header__note">{actionsNote}</p>
          </div>
        ) : actions ? (
          <div className="ui-page-header__actions">{actions}</div>
        ) : null}
      </header>
    </div>
  );
});
